import React from 'react'
import { createRoot, Root } from 'react-dom/client'
import { SnackBarItemData, SnackBarListBox, SnackBarVariant } from '@/components/content/SnackBar'
import { Utils } from '@/utils/Utils'

const SNACKBAR_ID = 'moms-snackbar'
const DEFAULT_DURATION = 3000
const CLOSE_ANIMATION_DURATION = 350 // max(slideOut 200ms, collapseOut delay 200ms + duration 150ms) = 350ms

class MSnackBarAction {
  private root: Root | null = null

  private list: SnackBarItemData[] = []

  private timers = new Map<string, ReturnType<typeof setTimeout>>()

  constructor() {
    this.setRoot()
  }

  private setRoot() {
    if (!document) return
    let $el = document.getElementById(SNACKBAR_ID)
    if (!$el) {
      $el = document.createElement('div')
      $el.id = SNACKBAR_ID
      document.body.appendChild($el)
    }
    this.root = createRoot($el)
  }

  private render() {
    this.root?.render(<SnackBarListBox list={this.list} onClose={this.close} />)
  }

  close = (id: string) => {
    this.clearTimer(id)
    this.list = this.list.map((item) => (item.id === id ? { ...item, closing: true } : item))
    this.render()
    setTimeout(() => {
      this.list = this.list.filter((item) => item.id !== id)
      this.render()
    }, CLOSE_ANIMATION_DURATION)
  }

  private clearTimer(id: string) {
    const timer = this.timers.get(id)
    if (timer) {
      clearTimeout(timer)
      this.timers.delete(id)
    }
  }

  show = (message: string, variant: SnackBarVariant = 'success', duration = DEFAULT_DURATION) => {
    const id = Utils.uuid()
    this.list = [...this.list, { id, message, variant }]
    this.render()
    if (duration > 0) {
      const timer = setTimeout(() => this.close(id), duration)
      this.timers.set(id, timer)
    }
    return id
  }

  success = (message: string) => this.show(message, 'success')

  warning = (message: string) => this.show(message, 'warning')

  error = (message: string) => this.show(message, 'critical')

  loading = (message: string) => this.show(message, 'loading', 0)
}

const MSnackBar = new MSnackBarAction()
export default MSnackBar
